import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { FlyingClubResult } from '../../shared/models/flying-club-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the Flying Club Finder, mirroring {@link EventResultsListingComponent}. Renders the
 * paged result cards (each linking to the existing `/flyingclub/:id` detail page), a filter popover, and
 * relays hover events for map sync.
 */
@Component({
  selector: 'app-flying-club-finder-results-listing',
  templateUrl: './flying-club-finder-results-listing.component.html',
  styleUrls: ['./flying-club-finder-results-listing.component.scss']
})
export class FlyingClubFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() flyingClubs: Array<FlyingClubResult>;
  @Input() state: State = State.Empty;
  @Input() activeFlyingClub: FlyingClubResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalFlyingClubs = 0;
  @Output() flyingClubMouseEnter = new EventEmitter<FlyingClubResult>();
  @Output() flyingClubMouseLeave = new EventEmitter<FlyingClubResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('flyingClubListEl') flyingClubListEl: ElementRef;
  @ViewChildren('flyingClubEl', { read: ElementRef }) flyingClubEls: QueryList<ElementRef>;
  @ViewChild('flyingClubFiltersPopover') flyingClubFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to a club's detail page, so that page's "Back"
   * button can return to these exact results (see FlyingclubPageComponent.goBack). Also clears the airport
   * back-target so the club opened from search doesn't fall back to "Back to <airport>".
   */
  onFlyingClubSelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('flyingClubSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onFlyingClubHover(flyingClub: FlyingClubResult, hover: boolean) {
    if (hover) {
      this.flyingClubMouseEnter.emit(flyingClub);
    } else {
      this.flyingClubMouseLeave.emit(flyingClub);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given flying club.
   */
  getFlyingClubEl(flyingClub: FlyingClubResult) {
    const i = this.flyingClubs.indexOf(flyingClub);
    if (i < 0) {
      return null;
    }
    const el = this.flyingClubEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified flying club (used for map hover sync).
   */
  public scrollToFlyingClub(flyingClub: FlyingClubResult) {
    if (!this.flyingClubListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.flyingClubListEl.nativeElement);
    const $el = this.getFlyingClubEl(flyingClub);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.flyingClubListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.flyingClubListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
